import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AdjustmentsHorizontalIcon, ChatBubbleLeftRightIcon, CubeIcon } from '@heroicons/react/24/outline'
import { ADRES_PROJEKTU_Z_ASYSTENTA } from '../../lib/projekt3d/asystent3d'
import { jestPolska } from '../../kraj'
import { KlodkaLogowania, useWymagaLogowania } from '../auth/BramkaLogowania'
import { KlodkaPremium, useWymagaPremium } from '../plany/BramkaPremium'

/**
 * Dwie drogi do tego samego silnika doboru zestawu, pokazane obok siebie.
 *
 * ═══ PO CO TO STOI W ŚRODKU ASYSTENTA, A NIE TYLKO W MENU ═══
 * Człowiek trafia tu z jednego przycisku na stronie głównej i dopiero na tym
 * ekranie widzi, o co pytamy. Jeśli pierwsze pole to „moc instalacji", a on tej
 * liczby nie zna, jego jedynym wyjściem jest cofnięcie się i szukanie innego
 * wejścia, o którym nie wie, że istnieje. Przełącznik stawia obie drogi tam,
 * gdzie ta wiedza jest potrzebna: w chwili odbicia się od pierwszego pytania.
 *
 * ═══ NAZWY W PIERWSZEJ OSOBIE ═══
 * „Na podstawie parametrów" i „na podstawie pytań praktycznych" opisują NASZ
 * mechanizm. „Podaję parametry" i „Odpowiadam na pytania" opisują to, co robi
 * człowiek, a to jest jedyne, co go w tym momencie interesuje.
 */
const DROGI = [
  {
    id: 'parametry', to: '/dobor-zestawu-pv', Icon: AdjustmentsHorizontalIcon,
    label: 'Podaję parametry', klucz: 'parametry',
    opis: 'Znam moc instalacji i pojemność magazynu',
  },
  {
    id: 'pytania', to: '/dobierz-zestaw', Icon: ChatBubbleLeftRightIcon,
    label: 'Odpowiadam na pytania', klucz: 'pytania',
    opis: 'Policzymy moc z Twojego domu i rachunków',
  },
  // Trzecia droga (prośba właściciela 2026-10-12): model domu w Projekcie 3D, a z niego wynik z powrotem do asystenta
  // („Zapisz i wróć do asystenta”, lib/projekt3d/asystent3d). Projekt 3D stoi na danych GUGiK, więc tylko w Polsce.
  // Od 2026-10-15 Projekt 3D jest po zalogowaniu: niezalogowany widzi kłódkę, a kafelek prowadzi do bramki na
  // `/projekt-3d?asystent=1`, która po zalogowaniu odsyła z powrotem pod ten sam adres.
  {
    id: 'model3d', to: ADRES_PROJEKTU_Z_ASYSTENTA, Icon: CubeIcon,
    label: 'Projektuję na modelu 3D', klucz: 'model3d', tylkoPolska: true, bramka: 'projekt3d',
    opis: 'Znajdę dom na mapie i sprawdzę, ile modułów się zmieści',
  },
]

const WyborDrogi = ({ aktywna }) => {
  const { t } = useTranslation('asystent')
  // Jedyna bramka w tych kafelkach to Projekt 3D, więc jeden hak (stała liczba wywołań przy każdym renderze).
  const zamknietyProjekt3d = useWymagaLogowania('projekt3d')
  // Projekt 3D jest w planie Premium (FE-04): zalogowany bez tej funkcji widzi plakietkę, kafelek prowadzi do
  // strony z oknem „Przejdź na Premium”.
  const premiumProjekt3d = useWymagaPremium('projekt3d')
  return (
  <div className={`mb-6 grid gap-3 ${jestPolska ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
    {DROGI.filter((d) => !d.tylkoPolska || jestPolska).map(({ id, to, Icon, klucz, bramka, label: labelPl, opis: opisPl }) => {
      const label = t(`drogi.${klucz}.label`, { defaultValue: labelPl })
      const opis = t(`drogi.${klucz}.opis`, { defaultValue: opisPl })
      const wybrana = id === aktywna
      const zamkniety = bramka === 'projekt3d' && zamknietyProjekt3d
      const premium = bramka === 'projekt3d' && premiumProjekt3d
      const wspolne = `flex items-center gap-3.5 rounded-2xl border-2 px-4 py-3.5 text-left
                       transition-all duration-150`
      // Droga, na której już jesteś, NIE jest odnośnikiem: kliknięcie w nią
      // przeładowałoby ten sam ekran i skasowało wpisane odpowiedzi.
      const tresc = (
        <>
          <span className={`flex items-center justify-center w-11 h-11 rounded-xl flex-shrink-0 ${
            wybrana ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600'
          }`}>
            <Icon className="w-6 h-6" />
          </span>
          <span className="min-w-0">
            <span className={`flex items-center gap-1.5 text-[15px] font-bold leading-tight ${
              wybrana ? 'text-blue-900' : 'text-gray-900'
            }`}>
              {label}
              {zamkniety && <KlodkaLogowania className="h-4 w-4" />}
              {premium && <KlodkaPremium />}
            </span>
            <span className="block text-xs text-gray-500 mt-0.5 leading-5">{opis}</span>
          </span>
        </>
      )
      return wybrana ? (
        <div key={id} aria-current="page"
             className={`${wspolne} border-blue-500 bg-blue-50/60 cursor-default`}>
          {tresc}
        </div>
      ) : (
        <Link key={id} to={to}
              className={`${wspolne} border-gray-200 bg-white hover:border-blue-300
                          hover:bg-blue-50/30 hover:shadow-sm`}>
          {tresc}
        </Link>
      )
    })}
  </div>
  )
}

export default WyborDrogi
